/**
 * Utility functions for date formatting
 */

// Trim excessive fractional seconds for Safari parsing and return Date
const parseIsoToDate = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return new Date(NaN);
  // e.g. 2025-08-27T06:20:48.4835497+00:00 -> 2025-08-27T06:20:48.483+00:00
  const cleaned = dateString.replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})$)/, '.$1');
  const d = new Date(cleaned);
  if (!isNaN(d)) return d;
  return new Date(dateString);
};

/**
 * Format a date string to "day Month YY at HH:MM" format
 * Example: "2025-07-03T17:35:00Z" -> "3 July 25 at 17:35"
 * 
 * @param {string} dateString - The date string to format
 * @param {boolean} [short=false] - When true, uses short month (e.g. "Aug") and non-padded hour (e.g. 6:20)
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, short = false) => {
  if (!dateString) return 'Unknown date';
  try {
    const date = parseIsoToDate(dateString);
    
    // Get day as number without leading zero
    const day = date.getDate();
    
    // Get month name (full vs short)
    const monthsFull = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = (short ? monthsShort : monthsFull)[date.getMonth()];
    
    // Get last 2 digits of year
    const year = date.getFullYear().toString().slice(-2);
    
    // Get time in HH:MM format
    const hours = short ? date.getHours().toString() : date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    // Format: "3 July 25 at 17:35"
    return `${day} ${month} ${year} at ${hours}:${minutes}`;
  } catch (e) {
    return 'Invalid date';
  }
};

/**
 * Formats a date with only date (no time)
 * Example: "2025-07-03T17:35:00Z" -> "3 July 2025"
 * 
 * @param {string} dateString - The date string to format
 * @returns {string} Formatted date string
 */
export const formatDateOnly = (dateString) => {
  if (!dateString) return 'Unknown date';
  try {
    const date = parseIsoToDate(dateString);
    
    // Get day as number without leading zero
    const day = date.getDate();
    
    // Get full month name
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const month = months[date.getMonth()];
    
    // Get full year
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  } catch (e) {
    return 'Invalid date';
  }
};
